import React, { useState, useEffect, useMemo } from "react";
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useToast,
} from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { t } from "i18next";
import * as yup from "yup";
import moment from "moment";
import { HSeparator } from "components/Separator/Separator";
import SearchEmployeeForm from "components/SearchEmployee/SearchEmployeeForm";
import {
  FIELD_SEARCH_EMPLOYEE_CODE,
  FIELD_SEARCH_EMPLOYEE_NAME,
  FIELD_SEARCH_EMPLOYEE_WORK_STATUS,
  FIELD_SEARCH_EMPLOYEE_TYPE,
  FIELD_SEARCH_EMPLOYEE_HIRING_DATE,
  FIELD_SEARCH_EMPLOYEE_ROLE_1,
  FIELD_SEARCH_EMPLOYEE_ROLE_2,
  FIELD_SEARCH_EMPLOYEE_JOB,
  FIELD_SEARCH_EMPLOYEE_BUSSINESS,
  FIELD_SEARCH_EMPLOYEE_DEPARTMENT,
  FIELD_SEARCH_EMPLOYEE_POSITION,
  FIELD_SEARCH_EMPLOYEE_DEPARMENT_LAYER_2,
  FIELD_SEARCH_EMPLOYEE_DEPARMENT_LAYER_3,
  FIELD_SEARCH_EMPLOYEE_DEPARMENT_LAYER_4,
  FIELD_SEARCH_EMPLOYEE_DEPARMENT_LAYER_5,
} from "constants";
import SearchEmployeeResult from "components/SearchEmployee/SearchEmployeeResult";
import { useMasterData } from "hooks/useMasterData";
import { customSearchEmployeeInput } from "utils";
import { useLoading } from "../../context/LoadingContext";
import { useEmployeeStore } from "store/employeeStore";
import { isAllEmployeeChecked } from "utils";

const ModalEmployees = ({
  isOpen,
  onClose,
  setParentFormValue,
  onSubmit,
  maxEmployee = null,
  isExportProcess = false,
}) => {
  // Use state instead redux store because of optimizing performance
  // Benchmark result:
  // - Redux store: around 70 ms
  // - State: around 0.001 to 0.005 ms
  const [employeeIdsCheckedState, setEmployeeIdsCheckedState] = useState([]);

  const schema = yup.object().shape({
    from: yup.string().nullable(),
    to: yup
      .string()
      .nullable()
      .test("error", t("messages.date_end"), (value, context) => {
        return !(value && new Date(value) < new Date(context.parent.from));
      }),
  });

  const {
    setSelectedEmployeeIds,
    resetSearchEmployee,
  } = useEmployeeStore();
  const {
    selectedEmployeeIds,
    allEmployees,
  } = useEmployeeStore((state) => state);

  // Only apply max employee limit when maxEmployee is explicitly provided (not null/undefined)
  const hasMaxEmployeeLimit = maxEmployee !== null && maxEmployee !== undefined;
  const effectiveMaxEmployee = hasMaxEmployeeLimit ? maxEmployee : null;

  const getMaxEmployeeErrorMessage = () =>
    t("pages.export.error_max_employees", { max: effectiveMaxEmployee });

  useEffect(() => {
    setEmployeeIdsCheckedState(selectedEmployeeIds);
  }, []);

  useEffect(() => {
    if (
      !hasMaxEmployeeLimit ||
      !effectiveMaxEmployee ||
      !Array.isArray(employeeIdsCheckedState)
    )
      return;

    if (employeeIdsCheckedState.length > effectiveMaxEmployee) {
      toast({
        status: "error",
        description: getMaxEmployeeErrorMessage(),
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      setEmployeeIdsCheckedState(
        employeeIdsCheckedState.slice(0, effectiveMaxEmployee)
      );
    }
  }, [employeeIdsCheckedState, hasMaxEmployeeLimit, effectiveMaxEmployee]);

  const methods = useForm({
    defaultValues: {
      name: "",
      from: "",
      to: "",
      work_status: "",
      role_1: "",
      job_codes: [],
      department: "",
      role_2: "",
      position_code: "",
      employment_type_code: "",
      business: "",
      code: "",
      department_2_codes: [],
    },
    resolver: yupResolver(schema),
  });

  const { data: masterData } = useMasterData();
  const toast = useToast();

  const closeModal = () => {
    methods.reset();
    resetFormValues(["from", "to"]);
    onSubmit({});
    resetSearchEmployee();
    onClose();
  };

  const resetFormValues = (fields) => {
    fields.forEach((field) => {
      methods.setValue(field, null);
    });
  };

  const { startLoading, stopLoading } = useLoading();

  const submitAndClose = () => {
    if (
      hasMaxEmployeeLimit &&
      effectiveMaxEmployee &&
      Array.isArray(employeeIdsCheckedState) &&
      employeeIdsCheckedState.length > effectiveMaxEmployee
    ) {
      toast({
        status: "error",
        description: getMaxEmployeeErrorMessage(),
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }
    startLoading();
    try {
      methods.reset();
      resetSearchEmployee();
      onClose();
      setTimeout(() => {
        const isCheckedAll = isAllEmployeeChecked(
          allEmployees,
          employeeIdsCheckedState
        );

        // Only set when all employees were checked
        // In case is not checked all wont set again because maybe 対象者 will need to display like: 全社員  -本田ひとみ1 (mean: all except: Honda Hitomi1) 
        if (isCheckedAll && setParentFormValue) {
          setParentFormValue("is_required_all", isCheckedAll);
        }

        setSelectedEmployeeIds(employeeIdsCheckedState);
      }, 500);
    } finally {
      setTimeout(() => {
        stopLoading();
      }, 500);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={closeModal} size="6xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t("pages.course_form.list_member")}</ModalHeader>
        <ModalCloseButton />
        <HSeparator />
        <ModalBody>
          <SearchEmployeeForm
            methods={methods}
            inputList={[
              FIELD_SEARCH_EMPLOYEE_CODE,
              FIELD_SEARCH_EMPLOYEE_NAME,
              FIELD_SEARCH_EMPLOYEE_WORK_STATUS,
              customSearchEmployeeInput(FIELD_SEARCH_EMPLOYEE_TYPE, {
                data: [{ id: "", name: "" }].concat(
                  masterData?.employment_types ?? []
                ),
              }),
              FIELD_SEARCH_EMPLOYEE_HIRING_DATE,
              FIELD_SEARCH_EMPLOYEE_ROLE_1,
              FIELD_SEARCH_EMPLOYEE_ROLE_2,
              customSearchEmployeeInput(FIELD_SEARCH_EMPLOYEE_JOB, {
                label: "pages.course_form.job_type",
                data: [{ id: "", name: "" }].concat(masterData?.jobs ?? []),
              }),
              customSearchEmployeeInput(
                FIELD_SEARCH_EMPLOYEE_DEPARMENT_LAYER_2,
                {
                  data: [{ id: "", name: "" }].concat(
                    masterData?.deparment_layer_2 ?? []
                  ),
                }
              ),
              customSearchEmployeeInput(
                FIELD_SEARCH_EMPLOYEE_DEPARMENT_LAYER_3,
                {
                  data: [{ id: "", name: "" }].concat(
                    masterData?.deparment_layer_3 ?? []
                  ),
                }
              ),
              customSearchEmployeeInput(
                FIELD_SEARCH_EMPLOYEE_DEPARMENT_LAYER_4,
                {
                  data: [{ id: "", name: "" }].concat(
                    masterData?.deparment_layer_4 ?? []
                  ),
                }
              ),
              customSearchEmployeeInput(
                FIELD_SEARCH_EMPLOYEE_DEPARMENT_LAYER_5,
                {
                  data: [{ id: "", name: "" }].concat(
                    masterData?.deparment_layer_5 ?? []
                  ),
                }
              ),
            ]}
            col={3}
            submitAndClose={submitAndClose}
            handleSubmit={methods.handleSubmit(onSubmit)}
            isModal
          />
          <SearchEmployeeResult
            setParentFormValue={setParentFormValue}
            maxHeigh="full"
            employeeIdsCheckedState={employeeIdsCheckedState}
            setEmployeeIdsCheckedState={setEmployeeIdsCheckedState}
            maxEmployee={isExportProcess ? maxEmployee : null}
          />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ModalEmployees;
